import React, { useEffect, useState } from 'react';
import api from '../../api/api';
import { DataGridBase } from '../../components/DataGrid/DataGridBase';
import {
    GridToolbarContainer,
    GridToolbarQuickFilter,
} from '@mui/x-data-grid-pro';
import { Tooltip } from '@mui/material';
import moment from 'moment';
import { toast } from 'react-toastify';
import { HiOutlineRefresh } from 'react-icons/hi';
import { knowAcces } from '../../utils/utils';

const ELENCO_LABELS = {
    0: 'Singola',
    1: 'HR Italia',
    2: 'HR Estero',
    3: 'Team',
    4: 'Extra Italia',
    5: 'Extra Estero',
};

const ESITO_COLORS = {
    pending: 'bg-gray-200 text-gray-700',
    sent: 'bg-blue-200 text-blue-800',
    delivered: 'bg-green-200 text-green-800',
    bounced: 'bg-red-200 text-red-800',
    dropped: 'bg-red-200 text-red-800',
    spamreport: 'bg-orange-200 text-orange-800',
    deferred: 'bg-yellow-200 text-yellow-800',
    unsubscribed: 'bg-orange-200 text-orange-800',
    blocked: 'bg-red-200 text-red-800',
};

const ESITO_LABELS = {
    pending: 'In coda',
    sent: 'Inviata',
    delivered: 'Consegnata',
    bounced: 'Bounce',
    dropped: 'Scartata',
    spamreport: 'Spam',
    deferred: 'Rinviata',
    unsubscribed: 'Disiscritto',
    blocked: 'Bloccata',
};

// Descrizioni mostrate come tooltip sulle chip di stato.
const ESITO_DESC = {
    pending: 'In coda: non ancora inviata.',
    sent: 'Inviata: accettata da SendGrid e in fase di consegna.',
    delivered: 'Consegnata: accettata dal server di posta del destinatario.',
    bounced: 'Bounce: indirizzo inesistente o rifiutato in modo permanente. Da correggere/rimuovere.',
    dropped: 'Scartata da SendGrid prima dell\'invio (es. indirizzo in blacklist o mittente non verificato).',
    spamreport: 'Segnalata come spam dal destinatario. Da non ricontattare.',
    deferred: 'Consegna rinviata temporaneamente dal server destinatario (riprova automatica).',
    unsubscribed: 'Il destinatario si è disiscritto. Da non ricontattare.',
    blocked: 'Bloccata dal server destinatario (es. reputazione mittente/IP), spesso temporanea.',
    aperte: 'Quanti destinatari hanno aperto l\'email (pixel di tracciamento; può essere sovra/sottostimato). Clicca per filtrare.',
    click: 'Quanti destinatari hanno cliccato almeno un link nell\'email (segnale di interesse più affidabile). Clicca per filtrare.',
};

function NewsletterToolbar({ canManage, retryableBlockedCount, retryDisponibile, retrying, retryBlocked, summary, activeFilter, toggleFilter, apertureCount, clickCount }) {
    return (
        <GridToolbarContainer>
            <div className='w-full flex justify-between'>
                <GridToolbarQuickFilter placeholder='Cerca...' />
                <div className='flex gap-2 text-sm'>
                    {canManage && retryableBlockedCount > 0 && (
                        <button
                            type='button'
                            className='success-button px-2 py-1 text-xs flex items-center gap-1 disabled:opacity-50'
                            onClick={retryBlocked}
                            disabled={retrying || !retryDisponibile}
                        >
                            <HiOutlineRefresh className='button-icon' />
                            {retrying ? 'Retry...' : `Riprova blocked (${retryableBlockedCount})`}
                        </button>
                    )}
                    {Object.entries(summary).map(([k, v]) => {
                        const fk = `esito:${k}`;
                        const isActive = activeFilter === fk;
                        return (
                            <button
                                key={k}
                                type='button'
                                onClick={() => toggleFilter(fk)}
                                className={`px-2 py-1 rounded text-xs ${ESITO_COLORS[k] || ''} ${isActive ? 'ring-2 ring-offset-1 ring-current font-semibold' : 'opacity-80 hover:opacity-100'}`}
                            >
                                {ESITO_LABELS[k] || k}: {v}
                            </button>
                        );
                    })}
                    <button
                        type='button'
                        onClick={() => toggleFilter('aperto')}
                        className={`px-2 py-1 rounded text-xs bg-teal-200 text-teal-800 ${activeFilter === 'aperto' ? 'ring-2 ring-offset-1 ring-current font-semibold' : 'opacity-80 hover:opacity-100'}`}
                    >
                        Aperte: {apertureCount}
                    </button>
                    <button
                        type='button'
                        onClick={() => toggleFilter('cliccato')}
                        className={`px-2 py-1 rounded text-xs bg-purple-200 text-purple-800 ${activeFilter === 'cliccato' ? 'ring-2 ring-offset-1 ring-current font-semibold' : 'opacity-80 hover:opacity-100'}`}
                    >
                        Click: {clickCount}
                    </button>
                </div>
            </div>
        </GridToolbarContainer>
    );
}

export const NewsletterDettaglio = ({ idNewsletter }) => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [retrying, setRetrying] = useState(false);
    const [activeFilter, setActiveFilter] = useState(null); // 'esito:sent' | 'aperto' | 'cliccato' | null
    const canManage = knowAcces(['admin', 'team_leader']);

    const load = async () => {
        if (!idNewsletter) return;
        try {
            setLoading(true);
            const r = await api.get(`/newsletter/dettaglio/${idNewsletter}`);
            setRows(r.data);
        } finally {
            setLoading(false);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); setActiveFilter(null); }, [idNewsletter]);

    // Deduplicazione: una riga per email (ultimo tentativo per id)
    const tentativiByEmail = rows.reduce((acc, row) => {
        const key = String(row.email || '').trim().toLowerCase();
        if (key) acc[key] = (acc[key] || 0) + 1;
        return acc;
    }, {});
    const deduplicatedRows = Object.values(
        rows.reduce((acc, row) => {
            const key = String(row.email || '').trim().toLowerCase();
            if (!key) return acc;
            if (!acc[key] || Number(row.id) > Number(acc[key].id)) acc[key] = row;
            return acc;
        }, {})
    ).map(r => ({
        ...r,
        tentativi: tentativiByEmail[String(r.email || '').trim().toLowerCase()] || 1,
    }));

    const summary = deduplicatedRows.reduce((acc, r) => {
        acc[r.esito] = (acc[r.esito] || 0) + 1;
        return acc;
    }, {});

    const apertureCount = deduplicatedRows.filter((r) => r.aperto).length;
    const clickCount = deduplicatedRows.filter((r) => r.cliccato).length;
    const retryableBlockedCount = deduplicatedRows.filter((r) => r.esito === 'blocked').length;
    const retryDisponibile = rows.some((r) => Number(r.retry_disponibile) === 1);

    const toggleFilter = (key) => setActiveFilter(prev => prev === key ? null : key);

    const displayedRows = (() => {
        if (!activeFilter) return deduplicatedRows;
        if (activeFilter === 'aperto') return deduplicatedRows.filter(r => r.aperto);
        if (activeFilter === 'cliccato') return deduplicatedRows.filter(r => r.cliccato);
        const esito = activeFilter.replace('esito:', '');
        return deduplicatedRows.filter(r => r.esito === esito);
    })();

    const retryBlocked = async () => {
        if (!idNewsletter || retryableBlockedCount === 0) return;
        const ok = window.confirm(`Riprovo l'invio ai ${retryableBlockedCount} destinatari attualmente blocked?`);
        if (!ok) return;
        try {
            setRetrying(true);
            const resp = await api.post(`/newsletter/dettaglio/${idNewsletter}/retry-blocked`);
            const { totale, inviati, falliti } = resp.data || {};
            toast.success(`Retry avviato: ${inviati || 0}/${totale || 0} accettati da SendGrid${falliti ? `, ${falliti} falliti` : ''}`);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.error || 'Errore durante il retry dei blocked');
        } finally {
            setRetrying(false);
        }
    };

    const columns = [
        { field: 'email', headerName: 'Email', flex: 1, minWidth: 200 },
        { field: 'nominativo', headerName: 'Nominativo', flex: 1 },
        {
            field: 'elenco',
            headerName: 'Elenco',
            width: 130,
            renderCell: (p) => ELENCO_LABELS[p.value] || p.value,
        },
        {
            field: 'esito',
            headerName: 'Esito',
            width: 160,
            renderCell: (p) => (
                <Tooltip
                    enterDelay={100}
                    enterNextDelay={0}
                    title={
                        <div>
                            <div>{ESITO_DESC[p.value]}</div>
                            {p.row.errore && <div className='mt-1 opacity-70 text-xs'>{p.row.errore}</div>}
                        </div>
                    }
                >
                    <span className={`px-2 py-1 rounded text-xs cursor-help ${ESITO_COLORS[p.value] || ''}`}>
                        {ESITO_LABELS[p.value] || p.value}
                        {p.row.tentativi > 1 && (
                            <span className='ml-1 opacity-70'>({p.row.tentativi} tent.)</span>
                        )}
                    </span>
                </Tooltip>
            ),
        },
        {
            field: 'aperto',
            headerName: 'Aperta',
            width: 90,
            type: 'boolean',
            renderCell: (p) => (
                <span className={`px-2 py-1 rounded text-xs ${p.value ? 'bg-teal-200 text-teal-800' : 'bg-gray-100 text-gray-500'}`}>
                    {p.value ? 'Sì' : 'No'}
                </span>
            ),
        },
        {
            field: 'ts_apertura',
            headerName: 'Data apertura',
            width: 160,
            renderCell: (p) => p.value ? moment(p.value).format('DD/MM/YYYY HH:mm') : '',
        },
        {
            field: 'cliccato',
            headerName: 'Click',
            width: 90,
            type: 'boolean',
            renderCell: (p) => (
                <span className={`px-2 py-1 rounded text-xs ${p.value ? 'bg-purple-200 text-purple-800' : 'bg-gray-100 text-gray-500'}`}>
                    {p.value ? 'Sì' : 'No'}
                </span>
            ),
        },
        {
            field: 'ts_click',
            headerName: 'Data click',
            width: 160,
            renderCell: (p) => p.value ? moment(p.value).format('DD/MM/YYYY HH:mm') : '',
        },
        {
            field: 'ts_invio',
            headerName: 'Inviato',
            width: 160,
            renderCell: (p) => p.value ? moment(p.value).format('DD/MM/YYYY HH:mm') : '',
        },
        {
            field: 'ts_ultimo_evento',
            headerName: 'Ultimo evento',
            width: 160,
            renderCell: (p) => p.value ? moment(p.value).format('DD/MM/YYYY HH:mm') : '',
        },
    ];

    return (
        <div className='page-container'>
            <div className='w-full h-[70vh] el-container'>
                <DataGridBase
                    storageKey={`newsletter-dettaglio-${idNewsletter}`}
                    slots={{ toolbar: NewsletterToolbar }}
                    slotProps={{
                        toolbar: {
                            canManage,
                            retryableBlockedCount,
                            retryDisponibile,
                            retrying,
                            retryBlocked,
                            summary,
                            activeFilter,
                            toggleFilter,
                            apertureCount,
                            clickCount,
                        },
                    }}
                    columns={columns}
                    rows={displayedRows}
                    loading={loading}
                    getRowId={(r) => r.id}
                />
            </div>
        </div>
    );
};

export default NewsletterDettaglio;
